import React from "react";
import Flag from "react-world-flags";
import { Typography } from "@mui/material";

/**
 * BrokerSelectionGrid Component
 *
 * A grid component that displays available brokers grouped by country.
 * Allows users to select a broker to add to their accounts.
 */
const BrokerSelectionGrid = ({ brokers, onBrokerSelect }) => {
  // Group brokers by country with proper data handling
  const groupedBrokers = brokers.reduce((acc, broker) => {
    // Use broker country info with fallbacks
    const country = broker.country || broker.country_name || "Global";
    const countryCode =
      broker.country_code ||
      (country === "India" ? "IN" : country === "United States" ? "US" : "");

    if (!acc[country]) {
      acc[country] = {
        brokers: [],
        country_code: countryCode,
      };
    }
    acc[country].brokers.push({
      ...broker,
      display_name: broker.display_name || broker.name, // Ensure consistent display name
    });
    return acc;
  }, {});

  return (
    // <div className="space-y-8 mx-auto px-4 sm:px-6 lg:px-8">
     <div className="w-full max-w-5xl space-y-8 mx-auto px-4 sm:px-6 lg:px-8"> {/* Added max-w-5xl */}
      <div className="text-center">
        <h2 className="ds-h2 text-fg-1 mb-4">
          Select Broker Platform
        </h2>
        <p className="text-fg-2 mb-8 max-w-2xl mx-auto">
          Choose your broker platform to connect your trading account. We
          support multiple brokers across different countries with secure API
          connections.
        </p>
      </div>

      {/* Responsive Grid with reasonable width constraints */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        {Object.entries(groupedBrokers).map(([country, groupData]) => (
          <div
            key={`country-${country}`}
            className="relative p-6 bg-bg-2 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-[var(--divider)] hover:border-cyan-200 dark:hover:border-cyan-700 hover:-translate-y-1 group min-h-[200px] w-full"
          >
            {/* Enhanced Country Header */}
            <div className="absolute -top-3 sm:-top-4 left-4 sm:left-6">
              <div className="relative inline-flex items-center">
                <div className="absolute inset-0 bg-bg-2 rounded-full -m-2 shadow-sm" />
                <span className="relative px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-bold bg-gradient-to-r from-cyan-500/10 to-cyan-500/20 dark:from-cyan-500/20 dark:to-cyan-500/30 text-cyan-600 dark:text-cyan-300 rounded-full border border-cyan-200 dark:border-cyan-600 uppercase tracking-wider backdrop-blur-sm">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    {groupData.country_code ? (
                      <Flag
                        code={groupData.country_code}
                        style={{
                          width: "20px",
                          height: "15px",
                          borderRadius: "2px",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                        }}
                        className="sm:w-6 sm:h-[18px]"
                      />
                    ) : (
                      <span className="text-sm sm:text-lg" title="Global">
                        🌎
                      </span>
                    )}
                    <span className="font-semibold text-xs sm:text-sm whitespace-nowrap">
                      {country}
                    </span>
                  </div>
                </span>
              </div>
            </div>

            {/* Broker Buttons Container */}
            <div className="mt-4 sm:mt-6 space-y-2 sm:space-y-3">
              {groupData.brokers.map((broker) => (
                <button
                  key={`${country}-${broker.id || broker.broker_id}`}
                  type="button"
                  onClick={() => onBrokerSelect(broker)}
                  className="btn btn-primary w-full"
                >
                  <div className="flex items-center justify-between w-full min-w-0">
                    <span className="flex-1 text-left truncate pr-2 min-w-0">
                      {broker.display_name || broker.name}
                    </span>
                    {broker.currency && (
                      <span className="ml-2 px-2 py-1 text-xs font-medium bg-black/10 dark:bg-white/10 rounded-md opacity-70 flex-shrink-0">
                        {broker.currency}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Card hover indicator */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
          </div>
        ))}
      </div>

      {/* Footer note */}
      <div className="text-center pt-6 lg:pt-8">
        <p className="text-sm lg:text-base text-fg-2">
          Can't find your broker?{" "}
          <a
            href="#"
            className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
          >
            Contact support
          </a>{" "}
          to request integration.
        </p>
      </div>
    </div>
  );
};

export default BrokerSelectionGrid;
